import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { AuthError, Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

type Profile = {
  id: string;
  user_id: string;
  display_name: string | null;
  brand_name: string | null;
  plan: string;
  avatar_url: string | null;
  onboarded_at: string | null;
  /**
   * How far the user has progressed through `/onboarding`. 0 = not entered;
   * 7 = terminal step. Lets us resume the wizard where they left off instead
   * of restarting the whole flow on reload.
   */
  onboarding_step: number;
  current_merchant_id: string | null;
};

export type MerchantRole = "owner" | "operator" | "viewer";

export type Membership = {
  id: string;
  merchant_id: string;
  role: MerchantRole;
  merchant: {
    id: string;
    name: string;
    slug: string;
    plan: string;
    logo_url: string | null;
    currency: string;
  };
};

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  /** True once the initial getSession() has resolved and Supabase is ready. */
  hydrated: boolean;
  /** True only when we have a user *and* their email is confirmed. */
  emailVerified: boolean;
  /** All merchants this user is a member of (across all roles). */
  memberships: Membership[];
  /** The merchant the user is currently acting on, derived from profile.current_merchant_id. */
  currentMerchant: Membership["merchant"] | null;
  merchantId: string | null;
  /** The caller's role within currentMerchant, or null if none selected. */
  currentRole: MerchantRole | null;
  role: MerchantRole | null;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshMemberships: () => Promise<void>;
  switchMerchant: (merchantId: string) => Promise<void>;
  resendVerificationEmail: () => Promise<{ error: AuthError | null; cooldown: number }>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Idle session timeout.
 *
 * Supabase refreshes access tokens automatically as long as the tab is alive
 * and the refresh token is still valid (30 days by default). We additionally
 * sign the user out after a fixed window of **no user activity** — this is a
 * defense-in-depth measure for shared laptops, not a security boundary.
 */
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const RESEND_COOLDOWN_MS = 60 * 1000;

// Events we treat as "user is actively using the app".
const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart"] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [loading, setLoading] = useState(true);
  const [hydrated, setHydrated] = useState(false);

  // Refs let the idle timer interact with the latest session value without
  // retriggering the outer effect (which would tear down the auth listener).
  const sessionRef = useRef<Session | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastResendRef = useRef<number>(0);

  const fetchProfile = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("user_id", userId)
      .maybeSingle();
    if (data) setProfile(data as unknown as Profile);
  }, []);

  const fetchMemberships = useCallback(async (userId: string) => {
    // Join merchants in-line. RLS on memberships already scopes rows to this
    // user; RLS on merchants allows members to SELECT.
    const { data, error } = await supabase
      .from("merchant_memberships")
      .select("id, merchant_id, role, merchant:merchants!inner(id, name, slug, plan, logo_url, currency)")
      .eq("user_id", userId);
    if (error) {
      // Don't blow up auth on a memberships read failure — the user can still
      // use the app without a merchant context; the UI will prompt.
      console.warn("Failed to load memberships", error);
      setMemberships([]);
      return;
    }
    setMemberships((data ?? []) as unknown as Membership[]);
  }, []);

  const doSignOut = useCallback(async (options?: { silent?: boolean; reason?: string }) => {
    await supabase.auth.signOut();
    setProfile(null);
    setMemberships([]);
    if (!options?.silent && options?.reason) {
      toast({ title: "Signed out", description: options.reason });
    }
  }, []);

  // --- Auth listener + initial hydration -----------------------------------
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      sessionRef.current = newSession;
      setSession(newSession);
      setUser(newSession?.user ?? null);

      if (event === "TOKEN_REFRESHED") return;

      if (event === "SIGNED_OUT") {
        setProfile(null);
        setMemberships([]);
        return;
      }

      if (newSession?.user) {
        const uid = newSession.user.id;
        setTimeout(() => {
          void fetchProfile(uid);
          void fetchMemberships(uid);
        }, 0);
      } else {
        setProfile(null);
        setMemberships([]);
      }
    });

    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      sessionRef.current = existing;
      setSession(existing);
      setUser(existing?.user ?? null);
      if (existing?.user) {
        void fetchProfile(existing.user.id);
        void fetchMemberships(existing.user.id);
      }
      setLoading(false);
      setHydrated(true);
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile, fetchMemberships]);

  // --- Idle timeout --------------------------------------------------------
  useEffect(() => {
    if (!session) return;

    const resetIdle = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        if (!sessionRef.current) return;
        void doSignOut({ reason: "You were signed out after 30 minutes of inactivity." });
      }, IDLE_TIMEOUT_MS);
    };

    resetIdle();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, resetIdle, { passive: true }));

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, resetIdle));
    };
  }, [session, doSignOut]);

  // --- Re-check session when the tab becomes visible -----------------------
  useEffect(() => {
    const onVisibility = async () => {
      if (document.visibilityState !== "visible") return;
      if (!sessionRef.current) return;
      const { data, error } = await supabase.auth.getSession();
      if (error || !data.session) {
        void doSignOut({ reason: "Your session expired. Please sign in again." });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [doSignOut]);

  async function signOut() {
    await doSignOut({ silent: true });
  }

  async function refreshProfile() {
    if (user) await fetchProfile(user.id);
  }

  async function refreshMemberships() {
    if (user) await fetchMemberships(user.id);
  }

  async function switchMerchant(merchantId: string) {
    if (!user) return;
    // Optimistic — reflect immediately in profile so UI re-renders,
    // then persist.
    setProfile((p) => (p ? { ...p, current_merchant_id: merchantId } : p));
    // `supabase.from()` already returns the widened type from
    // `client.ts` — no per-callsite cast needed. See that file for why.
    const { error } = await supabase
      .from("profiles")
      .update({ current_merchant_id: merchantId })
      .eq("user_id", user.id);
    if (error) {
      toast({ title: "Could not switch workspace", description: error.message });
      await fetchProfile(user.id); // revert
    }
  }

  async function resendVerificationEmail() {
    const now = Date.now();
    const elapsed = now - lastResendRef.current;
    if (elapsed < RESEND_COOLDOWN_MS) {
      return {
        error: null,
        cooldown: Math.ceil((RESEND_COOLDOWN_MS - elapsed) / 1000),
      };
    }
    if (!user?.email) {
      return { error: { message: "No email on file", name: "NoEmail" } as AuthError, cooldown: 0 };
    }
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: user.email,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    });
    if (!error) lastResendRef.current = now;
    return { error, cooldown: 0 };
  }

  const emailVerified = Boolean(user?.email_confirmed_at);

  // Derive the active merchant and role from profile + memberships. If
  // profile.current_merchant_id points at something the user isn't in (e.g.
  // they were just removed), fall back to the first membership.
  const { currentMerchant, currentRole } = useMemo(() => {
    if (memberships.length === 0) return { currentMerchant: null, currentRole: null };
    const targetId = profile?.current_merchant_id;
    const chosen =
      (targetId && memberships.find((m) => m.merchant_id === targetId)) || memberships[0];
    return {
      currentMerchant: chosen.merchant,
      currentRole: chosen.role,
    };
  }, [profile?.current_merchant_id, memberships]);

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        profile,
        loading,
        hydrated,
        emailVerified,
        memberships,
        currentMerchant,
        merchantId: currentMerchant?.id ?? null,
        currentRole,
        role: currentRole,
        signOut,
        refreshProfile,
        refreshMemberships,
        switchMerchant,
        resendVerificationEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

/**
 * Role helpers for UI-level gating (e.g., hide the "Invite" button from
 * viewers). Server-side authorization is enforced by RLS — these are UX
 * affordances, not security boundaries.
 */
export function canManageTeam(role: MerchantRole | null): boolean {
  return role === "owner";
}
export function canWrite(role: MerchantRole | null): boolean {
  return role === "owner" || role === "operator";
}
